import * as React from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from './skeleton';

export interface PageSkeletonProps {
  /**
   * The text a screen reader announces while the page loads. Defaults to
   * «Cargando…», the copy the bare loading page used to show.
   */
  label?: string;
  className?: string;
}

/**
 * A placeholder for a whole content area: a title bar and two card blocks.
 *
 * It exists so that loading keeps the page's frame instead of replacing it with
 * a centred line of text: the layout does not jump when the real content
 * arrives, and `AppLayout` can keep the shell on screen while the account
 * resolves.
 *
 * - Every shape comes from `Skeleton`, so each one is `aria-hidden`.
 * - The ONE announcement is a visually hidden `role="status"` /
 *   `aria-live="polite"` element. Nothing communicates by the pulse alone.
 */
export function PageSkeleton({
  label = 'Cargando…',
  className,
}: PageSkeletonProps): React.JSX.Element {
  return (
    <div className={cn('flex flex-col gap-stack', className)}>
      <span role="status" aria-live="polite" className="sr-only">
        {label}
      </span>
      <Skeleton variant="text" width="w-1/3" />
      {/* No card-height role exists in the tokens (inventory §9); the spacing
          scale is used, as the other block skeletons do (`h-40`). */}
      <Skeleton variant="block" height="h-40" />
      <Skeleton variant="block" height="h-40" />
    </div>
  );
}
